import type { LandingPageBody, LandingPageParams } from "@apptus/esales-api";
import { useMarket } from "@context/marketContext";
import getLandingPage from "@server/requests/elevate/getLandingPage";
import mapNgFilters from "@server/requests/elevate/mapNgFilters";
import mapNgSort from "@server/requests/elevate/mapNgSort";
import type { Attribute } from "@server/requests/product/fetchCategory";
import getSearchEngineUserUid from "@server/utils/getSearchEngineUserUid";
import type {
  BasicCommerceData,
  Filter,
  Loop54Content,
  Loop54Filter,
  Loop54State,
} from "@server/utils/parseLoop/types";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import type { CommerceData, Sort } from "types/common";
import type { LoopFacet } from "types/loop54";

import { FEATURE_FLAGS } from "@/config/featureFlags";
import { PRODUCTS_PER_PAGE } from "@/config/products";
import { generateHash, getInitialParams } from "@/utils/productSearch";

const useCategory = ({
  category,
  initialData,
  applyCustomFilter,
  commerceData,
  basicCommerceData,
  slug,
  pageNumber = 1,
  attribute,
  facets,
  // pageReference,
  // elevateFacets,
  // config,
  params,
  body,
}: {
  facets?: LoopFacet[];
  applyCustomFilter?: Loop54Filter[];
  category?: string;
  initialData?: Loop54Content;
  commerceData?: CommerceData[];
  basicCommerceData?: BasicCommerceData | null;
  attribute?: Attribute;
  slug: string;
  pageNumber?: number;
  // pageReference: string;
  // config?: LandingPageBody;
  // elevateFacets?: FacetParams;
  params: LandingPageParams;
  body?: LandingPageBody;
}) => {
  const router = useRouter();
  const marketConfig = useMarket().state.market;
  const [state, setState] = useState<Loop54State | undefined>();
  const [data, setData] = useState<Loop54Content | undefined>(initialData);

  const searchEngineUser = getSearchEngineUserUid();

  const getCategoryPayload = useCallback(() => {
    const { sort, filters } = getInitialParams(initialData);
    if (FEATURE_FLAGS.elevate) {
      return {
        ...params,
        // pageReference,
        sort: mapNgSort(sort),
        limit: PRODUCTS_PER_PAGE,
        skip: PRODUCTS_PER_PAGE * (pageNumber - 1),
        facets: { ...params.facets, ...mapNgFilters(filters) },
      };
    }

    return {
      attribute,
      marketConfig,
      facets,
      applyCustomFilter,
      category,
      commerceData,
      basicCommerceData,
      take: PRODUCTS_PER_PAGE,
      skip: PRODUCTS_PER_PAGE * (pageNumber - 1),
      sort,
      filters,
      slug,
      searchEngineUser,
    };
  }, [
    initialData,
    attribute,
    marketConfig,
    facets,
    applyCustomFilter,
    category,
    commerceData,
    basicCommerceData,
    pageNumber,
    slug,
    searchEngineUser,
    // pageReference,
    // elevateFacets,
    params,
  ]);

  const onChange = useCallback(() => {
    const { sort, filters } = getInitialParams(initialData);
    const payload = getCategoryPayload();

    getLandingPage(marketConfig, payload as LandingPageParams, body)
      .then(setData)
      .catch(() => console.error("Failed to fetch category"));

    setState({ sort, filters });
  }, [initialData, getCategoryPayload, marketConfig, body]);

  const updateFilter = (payload: Filter[]) => {
    const { sort } = getInitialParams(initialData);

    if (pageNumber && pageNumber > 1) {
      router.push(generateHash(sort, payload));
    } else {
      window.history.replaceState(
        window.history.state,
        "",
        generateHash(sort, payload),
      );
      onChange();
    }
  };

  const updateSort = (payload: Sort) => {
    const { filters } = getInitialParams(initialData);

    if (pageNumber && pageNumber > 1) {
      router.push(generateHash(payload, filters));
    } else {
      window.history.replaceState(
        window.history.state,
        "",
        generateHash(payload, filters),
      );
      onChange();
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash.length) {
      onChange();
    }
  }, [onChange, initialData]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const payload = getCategoryPayload();

    void getLandingPage(marketConfig, payload as LandingPageParams, body);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    data,
    state,
    updateSort,
    updateFilter,
  };
};

export default useCategory;
